import { createAsyncThunk, createSlice, isAnyOf } from "@reduxjs/toolkit";
import { ApiFilter } from "@result/rui/utils/api";

import ApiClient from "#/redux/api/ApiClient";
import { getErrorMessage } from "#/utils/APIUtils";

const baseUrl = "/api/dog/seme/vzorec";

export const createSeedAnalysis = createAsyncThunk(
  "seed/sample/create",
  async (dto, { rejectWithValue }) => {
    try {
      const response = await ApiClient.post(baseUrl, dto);
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const updateSeedAnalysis = createAsyncThunk(
  "seed/sample/update",
  async ({ dto, id }, { rejectWithValue }) => {
    try {
      const response = await ApiClient.put(`${baseUrl}/${id}`, dto);
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const getSeedAnalysisById = createAsyncThunk(
  "seed/sample/getById",
  async (id, { rejectWithValue }) => {
    try {
      const response = await ApiClient.get(`${baseUrl}/${id}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const getSeedAnalysisEvents = createAsyncThunk(
  "seed/getPage",
  async ({ animalId, page, sort }, { rejectWithValue }) => {
    try {
      const filter =
        animalId == null
          ? null
          : new ApiFilter().where("zivalId", "EQ", animalId);
      const response = await ApiClient.get(
        `${baseUrl}/page`,
        null,
        page,
        filter,
        sort
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const getSeedAnalysisOnDate = createAsyncThunk(
  "seed/getPageByDate",
  async ({ date, page, sort }, { rejectWithValue }) => {
    try {
      const filter = new ApiFilter().where("datumOdvzema", "EQ", date);

      const response = await ApiClient.get(
        `${baseUrl}/page`,
        null,
        page,
        filter,
        sort
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const getSeedAnalysisEventPage = createAsyncThunk(
  "seed/page",
  async ({ page, filter, sort }, { rejectWithValue }) => {
    try {
      const response = await ApiClient.get(
        `${baseUrl}/page`,
        null,
        page,
        filter,
        sort
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const getSeedAnalysisSequenceNumber = createAsyncThunk(
  "seed/getSequenceNumber",
  async ({ dateTaken }, { rejectWithValue }) => {
    try {
      const filter = new ApiFilter().and((andGroup) => {
        andGroup.where("datumOdvzema", "EQ", dateTaken);
      });

      const response = await ApiClient.get(
        `${baseUrl}/page`,
        null,
        { number: 0, size: 1 },
        filter,
        {
          zapStOdvzema: "desc",
        }
      );

      const lastSequence = response.data.content?.[0]?.zapStOdvzema ?? 0;

      return lastSequence + 1;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

export const validateSeedSequenceNumber = createAsyncThunk(
  "seed/validateSequenceNumber",
  async ({ dateTaken, sequenceNumber }, { rejectWithValue }) => {
    try {
      const filter = new ApiFilter().and((andGroup) => {
        andGroup.where("datumOdvzema", "EQ", dateTaken);
        andGroup.where("zapStOdvzema", "EQ", sequenceNumber);
      });

      const response = await ApiClient.get(
        `${baseUrl}/page`,
        null,
        { number: 0, size: 1 },
        filter,
        null
      );

      return response.data.content?.length === 0;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error));
    }
  }
);

const thunks = [
  createSeedAnalysis,
  updateSeedAnalysis,
  getSeedAnalysisEvents,
  getSeedAnalysisById,
  getSeedAnalysisSequenceNumber,
];

const seedAnalysisSlice = createSlice({
  name: "seed",
  initialState: {
    isLoading: false,
    error: null,
    page: {},
    details: {},
    draft: null,
  },
  reducers: {
    setSeedAnalysisDraft(state, action) {
      console.log("Save seed analysis draft");
      state.draft = action.payload;
    },
    resetSeedAnalysisDraft(state, _action) {
      console.log("Reset seed analysis draft");
      state.draft = null;
    },
    resetSeedAnalysisAssessment(state) {
      state.errors = null;
      state.isLoading = false;
    },
  },
  extraReducers: (builder) => {
    builder

      .addCase(createSeedAnalysis.fulfilled, (state, action) => {
        state.details = action.payload;
      })
      .addCase(getSeedAnalysisEvents.fulfilled, (state, action) => {
        state.page = action.payload;
      })
      .addCase(getSeedAnalysisEventPage.fulfilled, (state, action) => {
        state.page = action.payload;
      })
      .addCase(getSeedAnalysisById.fulfilled, (state, action) => {
        state.details = action.payload;
      })
      .addMatcher(isAnyOf(...thunks.map((t) => t.pending)), (state, _) => {
        state.error = null;
        state.isLoading = true;
      })
      .addMatcher(
        isAnyOf(...thunks.map((t) => t.rejected)),
        (state, action) => {
          state.error = action.payload;
          state.isLoading = false;
        }
      )
      .addMatcher(isAnyOf(...thunks.map((t) => t.fulfilled)), (state, _) => {
        state.isLoading = false;
      });
  },
});

export const {
  setSeedAnalysisDraft,
  resetSeedAnalysisDraft,
  resetSeedAnalysisAssessment,
} = seedAnalysisSlice.actions;

export default seedAnalysisSlice.reducer;
